import {CommsToggleButton, LoadingSpinner, SimpleDropdown} from 'common';
import {useMyUnreadConversationCount} from 'offer/hook/useMyUnreadConversationCount';
import {Heading, Pane, Table} from 'evergreen-ui';
import {Direction, Sort} from 'pagination';
import React, {useEffect, useMemo, useState, useCallback} from 'react';
import {BehaviorSubject} from 'rxjs';
import {useObservable} from 'rxsupport';
import {useSelectedReferral} from '../referral/domain/selectedReferral';
import {ReferralStatusComboBox} from './component/ReferralStatusComboBox';
import {ProviderAppActionPayload} from './core/reducer/action';
import {ProviderAppState} from './core/reducer/state';
import {useReferralDetailModal} from './detail';
import {ReferralFilter, ReferralFilterState, toGraphQlParams as filterStateToGraphQLParams} from './filter';
import {FilterBadges} from './filter/FilterBadges';
import {isValidCombination, QueryableReferralStatus, ReferralForProviderResult, useReferralQueryForProvider$} from './hook/useReferralQueryForProvider$';
import {StandardLayout} from './layouts/StandardLayout';
import {ReferralInquiryFormModal} from './referral/referral-inquiry';
import {
    AcceptedDateRenderer,
    isPreferred,
    PlacementType,
    Referral,
    ReferralListColumn,
    ReferralListHeaders,
    ReferralListRow,
    ScopeOfServiceRenderer,
    SelectedReferral,
    ServiceLocationType,
    ServiceType,
    ServiceTypeRenderer,
    SubmittedDateRenderer
} from 'referral';
import {Modal} from './modal/Modal';
import {RelayPagination, useRelayPagination} from './pagination';
import { FeatureFlaggable, FeatureFlagSwitch, ProviderPortalFeatureFlag, useFeatureFlagFilter } from 'featureflag';


const OPEN_COLUMNS: Readonly<FeatureFlaggable<ReferralListColumn>[]> = [
    {name: 'caseNumber', label: 'Referral ID', size: 1, basis: '3rem'},
    {name: 'clientName', label: 'Client Name', size: 2},
    {name: 'serviceTypes', label: 'Service Type', size: 3, render: ServiceTypeRenderer},
    {name: 'placementType', label: 'Scope of Services', size: 3, render: ScopeOfServiceRenderer},
    {name: 'county', label: 'County', size: 2},
    {name: 'location', label: 'Location', size: 2},
    {name: 'clientAge', label: 'Age', size: 1},
    {name: 'requiredLanguage', label: 'Required Language', size: 3},
    {name: 'communications', label: 'Communications', size: 1, basis: '3rem', featureFlag: ProviderPortalFeatureFlag.BHPNPP_3077_IS_TWO_WAY_MESSAGING_DISABLED, featureFlagNegate: true},
] as const;
const ACCEPTED_COLUMNS: Readonly<FeatureFlaggable<ReferralListColumn>[]> = [
    {name: 'caseNumber', label: 'Referral ID', size: 1, basis: '3rem'},
    {name: 'clientName', label: 'Client Name', size: 2},
    {name: 'serviceTypes', label: 'Service Type', size: 3, render: ServiceTypeRenderer},
    {name: 'placementType', label: 'Scope of Services', size: 3, render: ScopeOfServiceRenderer},
    {name: 'requestDate', label: 'Request Date', size: 2, render: SubmittedDateRenderer},
    {name: 'matchedDate', label: 'Accepted Date', size: 2, render: AcceptedDateRenderer},
    {name: 'location', label: 'Location', size: 2},
    {name: 'communications', label: 'Communications', size: 1, basis: '3rem', featureFlag: ProviderPortalFeatureFlag.BHPNPP_3077_IS_TWO_WAY_MESSAGING_DISABLED, featureFlagNegate: true},
] as const;

const useAutoResetSortWhenFilterChange = (
    currentSort$: BehaviorSubject<Sort<Referral>>,
    currentStatus$: BehaviorSubject<QueryableReferralStatus>,
) => {
    useEffect(() => {
        const sub = currentStatus$.subscribe((status) => {
            if (!isValidCombination(currentSort$.getValue(), status)) {
                currentSort$.next({
                    by: 'caseNumber',
                    direction: Direction.ASC,
                });
            }
        });
        return () => sub.unsubscribe();
    }, [currentStatus$, currentSort$]);
};


type ReferralPageProps = {
    appState: ProviderAppState,
    dispatch: React.Dispatch<ProviderAppActionPayload>,
    currentSort$: BehaviorSubject<Sort<Referral>>,
    currentStatus$: BehaviorSubject<QueryableReferralStatus>,
    currentFilter$: BehaviorSubject<ReferralFilterState>,
};
export const ReferralPage = ({
                                 appState,
                                 dispatch,
                                 currentSort$,
                                 currentStatus$,
                                 currentFilter$,
                             }: ReferralPageProps) => {
    useAutoResetSortWhenFilterChange(currentSort$, currentStatus$);

    // Sync appState.filter changes to currentFilter$ BehaviorSubject
    useEffect(() => {
        currentFilter$.next(appState.filter);
    }, [appState.filter, currentFilter$]);

    const selectedReferralUuid = useMemo(() => SelectedReferral.uuidFromQueryString(), []); 

    const {
        isRunning$,
        execute: refetchReferrals,
        data,
        error,
    } = useReferralQueryForProvider$(
        currentStatus$, currentSort$, appState.pagination.toGraphQlParams(),
        filterStateToGraphQLParams(appState.filter),
        selectedReferralUuid,
        appState.offerConversationOnly);
    const loading = useObservable(isRunning$, false);

    const { count: unreadConversationCount } = useMyUnreadConversationCount();
    const toggleOfferConversationOnly = useCallback(
        () => dispatch({ type: 'TOGGLE_OFFER_CONVERSATION_ONLY' }),
        [dispatch]
    );

    const {
        showReferralInquiry$,
        referralInquiryReferralGuid,
        referralInquiryReadOnly,
        referralInquirySubmittedDate,
        openReferralInquiry,
    } = useReferralInquiryModal(data);

    const setReferral = useReferralDetailModal(dispatch, openReferralInquiry);

    const openColumns = useFeatureFlagFilter(OPEN_COLUMNS);
    const acceptedColumns = useFeatureFlagFilter(ACCEPTED_COLUMNS);

    let totalReferrals: number = 0;
    if (data?.providerReferralQuery?.pageInfo?.endCursor !== null) {
        totalReferrals = data?.providerReferralQuery?.totalElements ?? 0;
    }

    const {onPaginationClick} = useRelayPagination(dispatch, totalReferrals);

    // BHPNPP-993 Group ABA and PLAY are not visible to provider users
    const visibleServiceTypes = [
        ServiceType.Traditional_ABA,
        ServiceType.ThreeTierOrParentTraining,
        ServiceType.ThreeTierOrParentLed,
        ServiceType.Parent_Led_ABA,
        ServiceType.ABA_Parent_Training,
        ServiceType.Social_Skills_Group,
        ServiceType.Chat,
        ServiceType.Connect,
        ServiceType.Calibrate,
        ServiceType.Compass_Behavioral,
        ServiceType.RUBI,
    ];
    const filterOption = {
        scopeOfServices: Object.keys(PlacementType),
        serviceTypes: visibleServiceTypes,
        serviceLocationTypes: Object.values(ServiceLocationType),
    };

    useSelectedReferral(setReferral, selectedReferralUuid, data);

    return (
        <StandardLayout>
            <Pane minHeight="56vh" flexGrow={1}>
                <Pane display='flex' padding={20}>
                    <Pane display='flex' flex={1} alignItems='center'>
                        <ReferralListHeading statusFilter$={currentStatus$} count={loading ? 0 : totalReferrals}/>
                    </Pane>
                    <ReferralFilter dispatch={dispatch} filterOption={filterOption} filter$={currentFilter$}/>
                    <SimpleDropdown options={["10", "50", "100"]} selected={appState.pagination.size.toString()}
                                    minWidth={"2rem"}
                                    textColor={"gray-700"}
                                    setSelected={(newPageSize) => dispatch({
                                        type: "SET_PAGE_SIZE",
                                        pageSize: parseInt(newPageSize)
                                    })}/>
                    <RelayPagination pageInfo={data?.providerReferralQuery.pageInfo} count={totalReferrals}
                                    onClick={onPaginationClick}/>
                    <div className="flex items-center gap-2">
                        <FeatureFlagSwitch flag={ProviderPortalFeatureFlag.BHPNPP_3077_IS_TWO_WAY_MESSAGING_DISABLED} negate>
                            <CommsToggleButton
                                active={appState.offerConversationOnly}
                                unreadCount={unreadConversationCount}
                                onClick={toggleOfferConversationOnly} />
                        </FeatureFlagSwitch>
                        <Pane>
                            <ReferralStatusComboBox statusFilter$={currentStatus$}/>
                        </Pane>
                    </div>
                </Pane>
                <div className='flex-grow'>
                    <FilterBadges filter={appState.filter} dispatch={dispatch}/>
                </div>
                <Table padding={20}>
                    <ReferralListHeaders paddingLeft={currentStatus$.getValue() === 'AVAILABLE' ? '36px' : '0px'}
                                        columns={currentStatus$.getValue() === 'AVAILABLE' ? openColumns : acceptedColumns}
                                        disableSortColumns={['county', 'communications']}
                                        currentSort$={currentSort$}
                                        isRunning$={isRunning$}/>
                    <Table.Body>
                        {(loading) && <LoadingSpinner centerScreen/>}
                        { !loading && !!data && data.providerReferralQuery.edges.length === 0 &&
                        <div className="text-center pt-5">No referrals matched the types of services and contracted areas your organization provides and/or the searching criteria.</div>
                        }
                        {!loading && !!data && !error && data.providerReferralQuery.edges.map(referral =>
                            <ReferralListRow
                                key={referral.node.guid}
                                columns={currentStatus$.getValue() === 'AVAILABLE' ? openColumns : acceptedColumns}
                                referral={referral.node}
                                selected={referral.node.guid === appState.referral?.guid}
                                chosen={referral.node.guid === selectedReferralUuid}
                                onSelect={() => setReferral(referral.node)}
                            />
                        )}
                    </Table.Body>
                </Table>
            </Pane>

            <Modal {...appState.modal} dispatch={dispatch} isPreferred={isPreferred(appState.referral)}>
                {appState.modal.children}
            </Modal>
            
            {/* Referral Inquiry Modal - rendered at page level for proper centering */}
            {referralInquiryReferralGuid && (
                <ReferralInquiryFormModal 
                    visible$={showReferralInquiry$} 
                    referralGuid={referralInquiryReferralGuid} 
                    dispatch={dispatch}
                    readOnly={referralInquiryReadOnly}
                    submittedDate={referralInquirySubmittedDate}
                    onSubmitted={refetchReferrals}
                />
            )}
        </StandardLayout>
    );
}


function useReferralInquiryModal( data: ReferralForProviderResult | undefined) {
    const [showReferralInquiry$, setShowReferralInquiry$] = useState(() => new BehaviorSubject<boolean>(false));
    const [referralInquiryReferralGuid, setReferralInquiryReferralGuid] = useState<string | null>(null);
    const [referralInquiryReadOnly, setReferralInquiryReadOnly] = useState(false);
    const [referralInquirySubmittedDate, setReferralInquirySubmittedDate] = useState<string | null>(null);

    const openReferralInquiry = useCallback((referralGuid: string) => {
        const referral = data?.providerReferralQuery.edges.find(({ node }) => node.guid === referralGuid)?.node;
        const firstRequest = referral?.incompleteReferralInquiryRequests?.[0];
        setReferralInquiryReferralGuid(referralGuid);
        setReferralInquiryReadOnly((referral?.incompleteReferralInquiryRequests?.length ?? 0) > 0);
        setReferralInquirySubmittedDate(firstRequest?.submittedDate ?? null);
        setShowReferralInquiry$(new BehaviorSubject<boolean>(true));
    }, [data]);

    return {
        showReferralInquiry$,
        referralInquiryReferralGuid,
        referralInquiryReadOnly,
        referralInquirySubmittedDate,
        openReferralInquiry,
    };
}

type ReferralListHeadingProps = {
    count: number;
    statusFilter$: BehaviorSubject<QueryableReferralStatus>
}
const ReferralListHeading = ({count, statusFilter$}: ReferralListHeadingProps) => {
    const currentStatus = useObservable(statusFilter$, 'AVAILABLE');
    let label = '';
    switch (currentStatus) {
        case 'ACCEPTED':
            label = 'Accepted';
            break;
        case 'AVAILABLE':
            label = 'Open';
            break;
        default:
            break;
    }
    return (<Heading size={600}>{label} Referrals ({count})</Heading>)
}
